import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, onValue, set, push, remove, update, get } from 'firebase/database';
import { changelogUpdates } from '../../data/changelogData';
import { useAuth } from '../../context/AuthContext';
import './AdminChangelog.css';

function AdminChangelog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { userProfile } = useAuth();

  // Form State
  const [formData, setFormData] = useState({
    version: '',
    date: '',
    type: 'feature',
    title: '',
    description: '',
    points: ''
  });

  useEffect(() => {
    const logsRef = ref(db, 'changelog');
    const unsubscribe = onValue(logsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const parsed = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        parsed.sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' }));
        setLogs(parsed);
      } else {
        setLogs([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSyncFromLocal = async () => {
    if (window.confirm("Synchronize platform nodes with local data? This will append missing versions.")) {
      const logsRef = ref(db, 'changelog');
      const existingSnap = await get(logsRef);
      const existing = existingSnap.exists() ? Object.values(existingSnap.val()) : [];
      
      for (const updateObj of changelogUpdates) {
        if (!existing.some(e => e.version === updateObj.version)) {
          const newRef = push(logsRef);
          await set(newRef, {
            ...updateObj,
            updatedAt: Date.now(),
            author: 'Neural-Sync'
          });
        }
      }
      alert("Neural sync complete.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      points: formData.points.split('\n').filter(p => p.trim() !== ''),
      updatedAt: Date.now(),
      author: userProfile?.name || 'Neural-Admin'
    };

    try {
      if (editingId) {
        await update(ref(db, `changelog/${editingId}`), dataToSave);
      } else {
        const logsRef = ref(db, 'changelog');
        await push(logsRef, dataToSave);
      }
      resetForm();
    } catch (err) {
      alert("Protocol failure: " + err.message);
    }
  };

  const resetForm = () => {
    setFormData({ version: '', date: '', type: 'feature', title: '', description: '', points: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (log) => {
    setFormData({
      version: log.version,
      date: log.date,
      type: log.type || 'feature',
      title: log.title,
      description: log.description,
      points: log.points.join('\n')
    });
    setEditingId(log.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, version) => {
    if (window.confirm(`Dissolve version ${version} from historical records?`)) {
      await remove(ref(db, `changelog/${id}`));
    }
  };

  return (
    <div className="admin-changelog-page animate-up">
      <div className="admin-header-stats">
        <div className="stat-node">
          <span className="stat-label">Platform Nodes</span>
          <span className="stat-value">{logs.length}</span>
        </div>
        <div className="stat-node">
          <span className="stat-label">Current Version</span>
          <span className="stat-value">{logs[0]?.version || 'v0.0.0'}</span>
        </div>
        <div className="stat-node">
          <span className="stat-label">System Health</span>
          <span className="stat-value health-ok">OPTIMAL</span>
        </div>
      </div>

      <div className="admin-page-header">
        <div>
          <h1>Neural Changelog Control</h1>
          <p>Architect the evolution of the DevTracker platform nodes.</p>
        </div>
        <div className="admin-header-actions">
          <button onClick={handleSyncFromLocal} className="admin-btn-secondary premium-glow">
            <i className="fa-solid fa-dna"></i> Neural Sync
          </button>
          <button onClick={() => setShowForm(!showForm)} className="admin-btn-primary premium-glow">
            <i className={`fa-solid ${showForm ? 'fa-circle-xmark' : 'fa-circle-plus'}`}></i> {showForm ? 'Abort' : 'Initiate Update'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="admin-form-container animate-down">
          <div className="admin-card changelog-form-card">
            <h3><i className="fa-solid fa-microchip"></i> {editingId ? 'Modify Version' : 'New Platform Node'}</h3>
            <div className="form-preview-layout">
              <form onSubmit={handleSubmit} className="premium-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Version ID</label>
                    <input type="text" value={formData.version} onChange={e => setFormData({...formData, version: e.target.value})} placeholder="e.g. v1.9.0" required />
                  </div>
                  <div className="form-group">
                    <label>Stamping Date</label>
                    <input type="text" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} placeholder="March 16, 2026" required />
                  </div>
                  <div className="form-group">
                    <label>Node Classification</label>
                    <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                      <option value="feature">✨ Neural Feature</option>
                      <option value="improvement">🚀 Optimization</option>
                      <option value="security">🛡️ Hardening</option>
                      <option value="fix">🐛 Node Patch</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Core Headline</label>
                    <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Headline summary" required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Broad Synthesis</label>
                  <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Synthesize this update in one statement..." required />
                </div>
                <div className="form-group">
                  <label>Log Points (Line-by-line)</label>
                  <textarea value={formData.points} onChange={e => setFormData({...formData, points: e.target.value})} placeholder="Feature details..." rows="5" required />
                </div>
                <div className="form-actions">
                  <button type="button" onClick={resetForm} className="btn-cancel">Cancel Protocol</button>
                  <button type="submit" className="btn-save">{editingId ? 'Update Node' : 'Initialize Node'}</button>
                </div>
              </form>

              <div className="live-preview-panel">
                <div className="preview-label">LIVE NEURAL PREVIEW</div>
                <div className={`preview-card ${formData.type}`}>
                  <div className="preview-header">
                    <span className="v-tag">{formData.version || 'vX.X.X'}</span>
                    <span className="v-date">{formData.date || 'TBD'}</span>
                  </div>
                  <h4>{formData.title || 'Incomplete Protocol'}</h4>
                  <p>{formData.description || 'Awaiting descriptive synthesis...'}</p>
                  <ul className="preview-points">
                    {(formData.points.split('\n').filter(p => p.trim() !== '') || ['Awaiting data points...']).slice(0, 3).map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                    {formData.points.split('\n').filter(p => p.trim() !== '').length > 3 && <li className="more-pts">+ More hidden...</li>}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="changelog-list-container">
        <h3 className="section-divider">Historical Chronology</h3>
        {loading ? (
          <div className="loading-state">Synthesizing historical data...</div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-box-open"></i>
            <p>Chronological records are empty. Initiate first node.</p>
          </div>
        ) : (
          <div className="logs-grid">
            {logs.map(log => (
              <div key={log.id} className={`admin-card log-item ${log.type}`}>
                <div className="log-item-header">
                  <div>
                    <span className={`log-badge ${log.type}`}>{log.version}</span>
                    <span className="log-date">{log.date}</span>
                  </div>
                  <div className="log-actions">
                    <button onClick={() => handleEdit(log)} className="edit-btn"><i className="fa-solid fa-sliders"></i></button>
                    <button onClick={() => handleDelete(log.id, log.version)} className="delete-btn"><i className="fa-solid fa-trash-can"></i></button>
                  </div>
                </div>
                <div className="log-content-body">
                  <h4>{log.title}</h4>
                  <p className="log-desc">{log.description}</p>
                  <div className="points-summary">
                    {log.points?.length ?? 0} nodes recorded
                  </div>
                </div>
                <div className="log-footer">
                  <span>Author: {log.author || 'Neural-Admin'}</span>
                  <span>{log.updatedAt ? new Date(log.updatedAt).toLocaleDateString() : (log.date || 'Historical Node')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminChangelog;
